import React from 'react';
import { callAll, Icon, Select } from '@brighterion/br-ui';
import { Field, FieldArrayRenderProps, FieldProps, useFormikContext } from 'formik';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';
import {
    FORBIDDEN_CURRENCY_CHARACTERS,
    FORBIDDEN_NUMERIC_CHARACTERS,
    FORBIDDEN_NUMERIC_INT_CHARACTERS,
    MAX_GLOBAL_FREE_ENTRY_LENGTH
} from 'Utils';
import { PARAMETERS_PARAM_TYPES, NEW_RULE_CONDITION_VALUE_TYPES, OPERATOR_CONDITIONS } from 'Common/enums';
import { FormikFieldFreeEntry, FormikInputField } from 'Components/FormikComponents';
import { ParameterTppType } from 'Common/parameterEnums';
import styles from './ValueField.css';

type ValuefieldProps = {
    arrayHelper: FieldArrayRenderProps;
    index: number;
    isThresholdEditorOnly: boolean;
    sortedListsByType: ListManagementApiList[];
    parameterTyppType: Parameter['tppType'];
    listData?: ListApiGetResponse;
};

const ValueField = ({
    arrayHelper,
    index,
    isThresholdEditorOnly,
    sortedListsByType,
    listData,
    parameterTyppType
}: ValuefieldProps) => {
    const { name, replace } = arrayHelper;
    const { t } = useTranslation();
    const {
        values: { conditions }
    } = useFormikContext<NewRuleFormSchema>();
    const { operatorField, parameterField } = conditions[index];
    const operator = operatorField[0];
    const parameterType = parameterField.type;

    return (
        <Field name={`${name}[${index}].valueField`}>
            {({ field, form, meta }: FieldProps) => {
                const isReadOnly = conditions[index].readOnly && isThresholdEditorOnly;

                switch (operator) {
                    // Text or Numeric Field
                    case OPERATOR_CONDITIONS.EQ:
                    case OPERATOR_CONDITIONS.NEQ:
                    case OPERATOR_CONDITIONS.LT:
                    case OPERATOR_CONDITIONS.LTEQ:
                    case OPERATOR_CONDITIONS.GT:
                    case OPERATOR_CONDITIONS.GTEQ:
                    case OPERATOR_CONDITIONS.STR_EQ:
                    case OPERATOR_CONDITIONS.STR_NEQ:
                    case OPERATOR_CONDITIONS.STR_CONTAINS:
                    case OPERATOR_CONDITIONS.STR_NOT_CONTAINS:
                        if (isReadOnly) {
                            return <div className={styles.readOnlyValue}>{field.value}</div>;
                        }
                        return (
                            <FormikInputField
                                name={field.name}
                                className={styles.valueInput}
                                type={parameterType === PARAMETERS_PARAM_TYPES.STRING ? 'text' : 'number'}
                                handleChange={callAll(
                                    () =>
                                        replace(index, {
                                            ...conditions[index],
                                            valueField: field.value,
                                            valueType:
                                                parameterType === PARAMETERS_PARAM_TYPES.STRING
                                                    ? PARAMETERS_PARAM_TYPES.STRING
                                                    : parameterType // CURRENCY or NUMBER
                                        }),
                                    form.handleChange // propagate changes to the form
                                )}
                                handleKeyDown={(event: React.KeyboardEvent) => {
                                    switch (parameterType) {
                                        case PARAMETERS_PARAM_TYPES.NUMBER:
                                            if (parameterTyppType === ParameterTppType.INT) {
                                                if (FORBIDDEN_NUMERIC_INT_CHARACTERS.includes(event.key)) {
                                                    event.preventDefault();
                                                }
                                            } else if (FORBIDDEN_NUMERIC_CHARACTERS.includes(event.key)) {
                                                event.preventDefault();
                                            }

                                            break;
                                        case PARAMETERS_PARAM_TYPES.CURRENCY:
                                            if (FORBIDDEN_CURRENCY_CHARACTERS.includes(event.key)) {
                                                event.preventDefault();
                                            }
                                            break;
                                        default:
                                            break;
                                    }
                                }}
                                handleBlur={form.handleBlur}
                                disabled={form.isSubmitting}
                            />
                        );

                    // FreeEntry Field
                    case OPERATOR_CONDITIONS.STR_IN:
                    case OPERATOR_CONDITIONS.STR_NOT_IN:
                        if (isReadOnly) {
                            return <div className={styles.readOnlyValue}>{field.value}</div>;
                        }

                        return (
                            <FormikFieldFreeEntry
                                name={field.name}
                                handleChange={() =>
                                    replace(index, {
                                        ...conditions[index],
                                        valueField: field.value.length === 0 ? '' : field.value.join(';'),
                                        valueType: NEW_RULE_CONDITION_VALUE_TYPES.FREE_ENTRY
                                    })
                                }
                                handleBlur={form.handleBlur}
                                className={styles.valueInput}
                                maxLength={MAX_GLOBAL_FREE_ENTRY_LENGTH}
                                disabled={form.isSubmitting}
                                // display error message when users click FreeEntry input field if exists
                                error={!!(meta.touched && meta.error)}
                                errorMsg={meta.error?.toString()}
                            />
                        );

                    // ListDropdown Field
                    case OPERATOR_CONDITIONS.STR_IN_LIST:
                    case OPERATOR_CONDITIONS.STR_NOT_IN_LIST:
                        if (isReadOnly) {
                            const listNamesWithIcons = field.value.map((listId: string) => (
                                <span key={listId} className={clsx(styles.listsWithIcons, styles.readOnlyValue)}>
                                    <Icon name='list' size='sm' className={styles.listIcon} />
                                    <span className={styles.listName}>
                                        {listData?.listIdHashMap[Number(listId)].name}
                                    </span>
                                </span>
                            ));
                            return <span>{listNamesWithIcons}</span>;
                        }

                        return (
                            <Select
                                name={field.name}
                                size='sm'
                                placeholder='- Select List -'
                                searchInputPlaceholder='Search'
                                handleClickCallback={(value) => {
                                    replace(index, {
                                        ...conditions[index],
                                        valueField: value,
                                        valueType: NEW_RULE_CONDITION_VALUE_TYPES.LIST
                                    });
                                    form.setFieldTouched(field.name, true);
                                }}
                                selectedItems={field.value ?? []}
                                type='listselector'
                                className={styles.listValueInput}
                                disabled={form.isSubmitting}
                                error={!!(meta.touched && meta.error)}
                                errorMsg={meta.error?.toString()}
                            >
                                {sortedListsByType.map((list: ListManagementApiList) => (
                                    <option key={list.name} value={list._formatted.stringId}>
                                        {list.name}
                                    </option>
                                ))}
                            </Select>
                        );

                    default:
                        return (
                            <span className={styles.nomatchingFieldPrompt}>{t('No Matching Input Field Found')}</span>
                        );
                }
            }}
        </Field>
    );
};

export default React.memo(ValueField);
